<template>
  <div class="home-media" :class="`is-${fit}`">
    <picture v-if="shouldShowImage">
      <source
        v-if="mobileSource"
        :srcset="sourceFailed && fallbackSource ? fallbackSource : mobileSource"
        media="(max-width: 767px)"
      />
      <img
        ref="imageElement"
        :src="imageSource || undefined"
        :alt="alt"
        :loading="loading"
        :fetchpriority="loading === 'eager' ? 'high' : 'low'"
        decoding="async"
        @load="handleImageLoad"
        @error="handleImageError"
      />
    </picture>
    <span v-else class="home-media-fallback">图片暂时无法加载</span>
  </div>
</template>

<script setup lang="ts">
  import { abortHtmlImage } from '~/utils/html-image'

  defineOptions({
    name: 'HomeMedia',
  })

  const props = withDefaults(
    defineProps<{
      alt: string
      fallbackSource?: string
      fit?: 'contain' | 'cover'
      loading?: 'eager' | 'lazy'
      mobileSource?: string
      source?: null | string
    }>(),
    {
      fallbackSource: '',
      fit: 'cover',
      loading: 'lazy',
      mobileSource: '',
      source: '',
    }
  )

  const sourceFailed = shallowRef(false)
  const hasFallbackFailure = shallowRef(false)
  const imageSource = computed(() =>
    sourceFailed.value && props.fallbackSource ? props.fallbackSource : props.source
  )
  const shouldShowImage = computed(() => Boolean(imageSource.value) && !hasFallbackFailure.value)
  const imageElement = shallowRef<HTMLImageElement>()
  let fallbackTimer: ReturnType<typeof setTimeout> | undefined
  let loadAborted = false

  function clearFallbackTimer() {
    if (fallbackTimer) clearTimeout(fallbackTimer)
    fallbackTimer = undefined
  }

  /** 取消尚未完成的封面下载，避免离页后仍占用带宽。 */
  function abortImageLoad() {
    const image = imageElement.value
    if (!image || image.complete) return
    loadAborted = true
    abortHtmlImage(image)
  }

  function scheduleFallback() {
    clearFallbackTimer()
    if (!props.fallbackSource || !props.source || !/^https?:\/\//.test(props.source)) return
    fallbackTimer = setTimeout(() => {
      if (loadAborted) return
      if (!imageElement.value?.complete || imageElement.value.naturalWidth === 0) {
        sourceFailed.value = true
      }
    }, 2200)
  }

  function handleImageLoad(event?: Event) {
    if (loadAborted || (event?.target && event.target !== imageElement.value)) return
    clearFallbackTimer()
  }

  function handleImageError(event?: Event) {
    if (loadAborted || (event?.target && event.target !== imageElement.value)) return
    if (!imageElement.value?.getAttribute('src')) return
    if (!sourceFailed.value && props.fallbackSource && props.fallbackSource !== props.source) {
      sourceFailed.value = true
      return
    }
    if (sourceFailed.value && imageElement.value?.getAttribute('src') !== props.fallbackSource) {
      return
    }
    hasFallbackFailure.value = true
  }

  function checkImageLoadState() {
    const image = imageElement.value
    if (image?.complete && image.naturalWidth === 0) handleImageError()
  }

  onMounted(() => {
    void nextTick(() => {
      checkImageLoadState()
      scheduleFallback()
    })
  })

  onBeforeUnmount(() => {
    abortImageLoad()
    clearFallbackTimer()
  })

  watch(
    () => [props.source, props.mobileSource, props.fallbackSource],
    () => {
      abortImageLoad()
      loadAborted = false
      sourceFailed.value = false
      hasFallbackFailure.value = false
      void nextTick(() => {
        checkImageLoadState()
        scheduleFallback()
      })
    }
  )
</script>

<style scoped lang="scss">
  .home-media {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: var(--surface-soft);
  }

  picture,
  img {
    display: block;
    width: 100%;
    height: 100%;
  }

  img {
    object-fit: cover;
  }

  .is-contain img {
    // padding: 18px;
    object-fit: cover;
  }

  .home-media-fallback {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    padding: 24px;
    font-size: 14px;
    color: var(--muted);
    text-align: center;
  }
</style>
